import { Component, h, Prop, State, Element } from '@stencil/core';
import classnames from 'classnames';
import _debounce from 'lodash/debounce';
import Slider from './Slider';

@Component({
    tag: 'axl-carousel',
    styleUrl: 'carousel.scss',
})

export class Carousel {

    constructor() {
        this.debounceHandleSettle = this.debounceHandleSettle.bind(this);
        this.scrollLeft = this.scroll.bind(this, 'left');
        this.scrollRight = this.scroll.bind(this, 'right');
    }

    @Element() host: HTMLDivElement;

    @Prop() items: Array<Object> = [];
    @Prop() fullWidth: boolean = false;
    @Prop() fullHeight: boolean = false;
    @Prop() cellWidth: string = "";
    @Prop() renderCell: Function = () => <div>placeholder</div>;
    @Prop() callbackOnReady: Function = () => { };
    @Prop() callbackOnSettle: Function = () => { };
    @Prop() renderTitle?: Function = () => { };

    @State() disableLeftButton = true;
    @State() disableRightButton = false;

    private slider = null;

    private carouselRef = null;
    private intersectionObserver?: IntersectionObserver;

    // bounded 
    private scrollLeft;
    private scrollRight;

    renderCellContainers() {
        return this.items.map(
            (item, index) => {
                let inlineStyles = null;
                if (this.cellWidth) {
                    inlineStyles = {
                        width: this.cellWidth,
                        flex: `0 0 ${this.cellWidth}`,
                        position: 'relative',
                    };
                }
                const classNames = classnames({
                    'text-center': true,
                    'carousel-cell': true,
                    'carousel-full-width': this.fullWidth,
                    'display-flex': this.fullHeight,
                    'justify-content-center': this.fullHeight,
                });

                return (
                    <div
                        class={classNames}
                        style={inlineStyles}
                    >
                        {this.renderCell(item, index)}
                    </div>
                );
            }
        );
    };

    toggleButtons(prevElement, nextElement) {
        // if there is no sibling elements, disable the button
        const shouldDisableRightButton = !nextElement;
        const shouldDisableLeftButton = !prevElement;

        // only update button if necessary
        if (shouldDisableRightButton !== this.disableRightButton) {
            this.disableRightButton = shouldDisableRightButton;
        }

        if (shouldDisableLeftButton !== this.disableLeftButton) {
            this.disableLeftButton = shouldDisableLeftButton;
        }
    }

    handleSettle() {
        this.slider.updateAfterScroll(({
            direction,
            width,
            selectedCells,
            selectedIndicies,
            nextElement,
            prevElement,
        }) => {

            this.toggleButtons(prevElement, nextElement);
            this.callbackOnSettle({ direction, width, selectedCells, selectedIndicies });

        });
    };

    private debounceHandleSettle = _debounce(this.handleSettle, 200);

    private handleResize = _debounce(() => {
        if (this.slider) {
            this.slider.init((data) => {
                this.toggleButtons(data?.prevElement, data?.nextElement);
            });
        }
    }, 100);

    scroll(direction) {
        if (direction === 'left') {
            this.slider.scrollLeft();
        } else {
            this.slider.scrollRight();
        }
    }

    componentDidRender() {

        if (!this.slider) {
            this.slider = new Slider(this.carouselRef);

            this.slider.init((data) => {
                this.toggleButtons(data?.prevElement, data?.nextElement);
                this.callbackOnReady(data);
            });

            this.carouselRef.addEventListener('scroll', this.debounceHandleSettle);
            
            // Add resize listener
            window.addEventListener('resize', this.handleResize);
            
            // Add intersection observer for off-screen detection
            this.intersectionObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && this.slider) {
                        // Use requestAnimationFrame to ensure DOM is fully rendered and measured
                        requestAnimationFrame(() => {
                            if (this.slider) {
                                this.slider.init((data) => {
                                    this.toggleButtons(data?.prevElement, data?.nextElement);
                                });
                            }
                        });
                    }
                });
            }, {
                // Add threshold to ensure we detect when component is actually visible
                threshold: 0.1,
                // Use document as root to work properly in all contexts
                root: null
            });
            
            this.intersectionObserver.observe(this.host);
        }
    }

    disconnectedCallback() {
        this.carouselRef.removeEventListener('scroll', this.debounceHandleSettle);
        window.removeEventListener('resize', this.handleResize);
        this.intersectionObserver?.disconnect();
    }

    render() {
        const buttonClass = 'carousel-container carousel-nav-button padding-0';

        return (
            <div class="carousel-container">
                {this.renderTitle()}
                <div
                    class="carousel-container-inner"
                    ref={el => this.carouselRef = el as HTMLInputElement}
                >
                    {this.renderCellContainers()}
                </div>
                <button
                    type="button"
                    class={classnames(buttonClass, 'previous')}
                    onClick={this.scrollLeft}
                    aria-label="Previous"
                    disabled={this.disableLeftButton}
                >
                    <axl-glyphicon
                        glyphClass="text-center text-size-700"
                        glyph="menu-left" />
                </button>
                <button
                    type="button"
                    class={classnames(buttonClass, 'next')}
                    onClick={this.scrollRight}
                    aria-label="Next"
                    disabled={this.disableRightButton}
                >
                    <axl-glyphicon
                        glyphClass="text-center text-size-700"
                        glyph="menu-right"
                    />
                </button>
            </div>
        );
    }

}